import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, HelpCircle, ExternalLink, BarChart3, Newspaper, Landmark, MapPin, ShieldAlert, PiggyBank, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Shared';

const STATUS_META = {
  identified: { label: 'Identified', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  under_review: { label: 'Under Review', cls: 'bg-amber-50 text-amber-900 border-amber-300' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-900 border-emerald-300' },
  proposed: { label: 'Proposed', cls: 'bg-sky-50 text-sky-900 border-sky-300' },
  council_action: { label: 'Council Action', cls: 'bg-violet-50 text-violet-900 border-violet-300' },
  savings_achieved: { label: 'Savings Achieved', cls: 'bg-emerald-100 text-emerald-950 border-emerald-400' },
  // legacy statuses (existing items keep working)
  needs_review: { label: 'Needs Review', cls: 'bg-amber-50 text-amber-900 border-amber-300' },
  source_based: { label: 'Source-Based', cls: 'bg-sky-50 text-sky-900 border-sky-300' },
  in_progress: { label: 'In Progress', cls: 'bg-violet-50 text-violet-900 border-violet-300' },
  resolved: { label: 'Resolved', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const AMOUNT_META = {
  reported: { label: 'Reported', cls: 'bg-amber-50 text-amber-900 border-amber-300' },
  estimated: { label: 'Estimated', cls: 'bg-orange-50 text-orange-900 border-orange-300' },
  source_needed: { label: 'Source Needed', cls: 'bg-red-50 text-red-900 border-red-200' },
  source_based: { label: 'Source-Based', cls: 'bg-sky-50 text-sky-900 border-sky-300' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-900 border-emerald-300' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.under_review;
  return <Badge variant="outline" className={`${m.cls} font-bold text-[11px]`} data-testid={`savings-status-${status}`}>{m.label}</Badge>;
};

const AmountBadge = ({ label }) => {
  const m = AMOUNT_META[label] || AMOUNT_META.source_needed;
  return <Badge variant="outline" className={`${m.cls} font-bold text-[11px]`} data-testid={`savings-amount-label-${label}`}>{m.label}</Badge>;
};

export default function SavingsTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    api.get('/savings').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const items = useMemo(() => (data?.items || []).filter((i) =>
    (category === 'all' || i.category === category) &&
    (status === 'all' || i.status === status)
  ), [data, category, status]);

  const categories = data?.categories || {};

  return (
    <div>
      <PageHeader pageKey="savings-tracker" kicker="TAXPAYER VALUE" title="Savings Tracker" subtitle={data?.tagline || 'Finding waste. Asking questions. Fighting for better value.'} />

      {/* Combined accountability band */}
      <div className="bg-[#C9A84C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="font-serif font-bold text-[#1a2744] text-sm sm:text-base leading-snug" data-testid="savings-band">
            Report it. Map it. Track it. Cut the waste. Lower the taxes.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 overflow-x-hidden">
        {data?.intro && <p className="text-slate-700 leading-relaxed max-w-3xl" data-testid="savings-intro">{data.intro}</p>}

        {/* Disclaimer — always visible */}
        <Card className="mt-4 border-amber-200 bg-amber-50/70">
          <CardContent className="p-4 flex items-start gap-2.5" data-testid="savings-disclaimer">
            <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900 leading-relaxed">{data?.disclaimer || 'Figures shown in the Savings Tracker may be reported, estimated, or verified. Each item should be reviewed with its source before being treated as confirmed.'}</p>
          </CardContent>
        </Card>

        {/* How the pipeline works: methodology, always public */}
        {data?.stages?.length > 0 && (
          <Card className="mt-4" data-testid="savings-methodology">
            <CardContent className="p-4 sm:p-5">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">How items move through the tracker (methodology)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {data.stages.map((s, i) => (
                  <div key={s.key} className="flex items-start gap-2.5 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid={`savings-stage-${s.key}`}>
                    <span className="inline-flex items-center justify-center h-6 w-6 shrink-0 rounded-full bg-[#1a2744] text-[#C9A84C] text-[11px] font-black mt-0.5">{i + 1}</span>
                    <div>
                      <p className="text-xs font-black text-[#1a2744] uppercase tracking-wide">{s.label}</p>
                      <p className="text-xs text-slate-600 leading-snug">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-testid="savings-methodology-note">Hypothetical savings are never counted as achieved. An item only reaches Savings Achieved with a verified, source-linked amount from reliable records.</p>
            </CardContent>
          </Card>
        )}

        {/* Savings Achieved: verified realized savings only */}
        {data?.achieved_total && (
          <Card className="mt-4 border-emerald-300 bg-emerald-50/50" data-testid="savings-achieved-counter">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                <PiggyBank className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">Savings Achieved (verified)</p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span className="text-2xl font-black text-emerald-950" data-testid="savings-achieved-amount">{data.achieved_total.display}</span>
                  <span className="text-xs text-emerald-800 font-semibold">across {data.achieved_total.count} verified item{data.achieved_total.count === 1 ? '' : 's'}</span>
                </div>
                <p className="text-xs text-emerald-800 mt-1 leading-relaxed">{data.achieved_total.note}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Total Flagged for Review — appears ONLY when source-linked figures exist */}
        {data?.total_flagged && (
          <Card className="mt-4" data-testid="savings-total-counter">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#1a2744] flex items-center justify-center shrink-0">
                <PiggyBank className="h-5 w-5 text-[#C9A84C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Flagged for Review</p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span className="text-2xl font-black text-[#1a2744]" data-testid="savings-total-amount">{data.total_flagged.display}</span>
                  <AmountBadge label={data.total_flagged.label} />
                  <span className="text-xs text-slate-500 font-semibold">across {data.total_flagged.count} source-linked item{data.total_flagged.count === 1 ? '' : 's'}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed" data-testid="savings-total-note">{data.total_flagged.note}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:w-2/3 gap-3 mt-5" data-testid="savings-filters">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 bg-white" data-testid="savings-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(categories).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 bg-white" data-testid="savings-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Items */}
        <div className="mt-6 space-y-4">
          {loading ? (
            <><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" /></>
          ) : items.length === 0 ? (
            <EmptyState title="No items match these filters" subtitle="Flagged spending and savings items appear here as Steve reviews City spending, systems, and contracts." />
          ) : items.map((item) => (
            <Card key={item.id} data-testid="savings-item">
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300 font-semibold text-[11px]">{categories[item.category] || item.category}</Badge>
                  <StatusBadge status={item.status} />
                </div>
                <h2 className="font-serif font-black text-[#1a2744] text-lg sm:text-xl mt-2 leading-snug" data-testid="savings-item-title">{item.title}</h2>

                {/* Amount with mandatory truth label */}
                <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="savings-item-amount">
                  {item.amount ? (
                    <>
                      <span className="text-xl font-black text-[#1a2744]">{item.amount}</span>
                      <AmountBadge label={item.amount_label} />
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                      Cost figure pending official sources <AmountBadge label={item.amount_label} />
                    </span>
                  )}
                </div>

                {item.summary && <p className="text-slate-700 mt-3 leading-relaxed break-words" data-testid="savings-item-summary">{item.summary}</p>}

                {item.why_it_matters && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3.5" data-testid="savings-item-why">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Why it matters</p>
                    <p className="text-sm text-slate-700 leading-relaxed break-words">{item.why_it_matters}</p>
                  </div>
                )}

                {item.question && (
                  <div className="mt-3 bg-[#1a2744] rounded-xl p-3.5" data-testid="savings-item-question">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#C9A84C] mb-1 flex items-center gap-1"><HelpCircle className="h-3 w-3" /> The question Steve is asking</p>
                    <p className="text-sm text-white leading-relaxed break-words">{item.question}</p>
                  </div>
                )}

                {/* Source */}
                <div className="mt-3 text-sm" data-testid="savings-item-source">
                  {item.source_url ? (
                    <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4">
                      <ExternalLink className="h-3.5 w-3.5" /> {item.source_name || 'View source document'}
                    </a>
                  ) : (
                    <span className="text-slate-500 italic">Data source needed.</span>
                  )}
                </div>

                {/* Related content — only shown when the target is published */}
                {(item.related_chart || item.related_article || item.related_meeting || item.related_issue_category) && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100" data-testid="savings-item-related">
                    {item.related_chart && (
                      <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold btn-press" data-testid="savings-related-chart">
                        <Link to={`/city-data?chart=${item.related_chart.id}`}><BarChart3 className="h-3.5 w-3.5 mr-1" /> {item.related_chart.title}</Link>
                      </Button>
                    )}
                    {item.related_article && (
                      <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold btn-press" data-testid="savings-related-article">
                        <Link to={`/newsroom/${item.related_article.slug}`}><Newspaper className="h-3.5 w-3.5 mr-1" /> {item.related_article.title}</Link>
                      </Button>
                    )}
                    {item.related_meeting && (
                      <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold btn-press" data-testid="savings-related-meeting">
                        <Link to="/meetings"><Landmark className="h-3.5 w-3.5 mr-1" /> {item.related_meeting.title}</Link>
                      </Button>
                    )}
                    {item.related_issue_category && (
                      <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold btn-press" data-testid="savings-related-issues">
                        <Link to={`/map?category=${item.related_issue_category}`}><MapPin className="h-3.5 w-3.5 mr-1" /> Related resident reports</Link>
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Follow the Savings Tracker — newsletter block */}
        <Card className="mt-6 border-[#C9A84C]/50 bg-[#FFFBF0]" data-testid="savings-newsletter-block">
          <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-[#C9A84C] flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-[#1a2744]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif font-black text-[#1a2744] text-lg leading-tight">Follow the Savings Tracker</p>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">Residents can follow what spending questions, cost concerns, and savings opportunities Steve is tracking.</p>
            </div>
            <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid="savings-subscribe-button">
              <Link to="/alerts">Subscribe for Updates</Link>
            </Button>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 bg-[#1a2744] rounded-2xl p-6 sm:p-8" data-testid="savings-cta">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#C9A84C] mb-2">SEEING WASTE OR POOR SERVICE?</p>
          <p className="text-white font-serif font-bold text-lg leading-relaxed">One complaint can be overlooked. A map full of complaints cannot.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="savings-report-btn"><Link to="/issue-tracker/report">Report an Issue</Link></Button>
            <Button asChild variant="outline" className="h-12 border-white bg-transparent text-white hover:bg-white/10 font-bold btn-press" data-testid="savings-citydata-btn"><Link to="/city-data"><BarChart3 className="h-4 w-4 mr-1.5" /> Explore City Data</Link></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
